import { Component, OnInit, inject, DestroyRef, input, model } from '@angular/core';
import { AdminEntity, BokkzClient, ClientEntity, IntermediaryEntity } from '../services/api.bokkz.services';
import { CustomerResponse, KolommenbalansService } from '../services/rekenclient.api';
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { BokkzAlgDataService } from '../services/bokkz-alg-data-service';
import { combineLatest, distinctUntilChanged, filter, map, Observable, shareReplay, startWith, take, tap } from 'rxjs';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { SharedUiDialogModule } from '@snelstart/ui-dialog';
import { SharedUiTabModule } from '@snelstart/ui-tab';
import { SharedUiAlertModule } from '@snelstart/ui-alert';
import { CommonModule } from '@angular/common';
import { BalanceProviderAdministrationSelectionDialogComponent } from '../balance-providers/administration-selection-dialog/administration-selection-dialog.component';
import { BalanceProviderAdministrationSelectionContext } from '../balance-providers/administration-selection-dialog/administration-selection-dialog.model';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { createAdministrationFormGroup, createAdministrationsFormGroup, createClientFormGroup, createCustomerFormGroup, patchAdministrationFormGroup } from './client.form';
import { BEDRIJFSKLASSE_OPTIONS, BOOLEAN_OPTIONS, SBI_CODE_OPTIONS, toAdminEntitySnelStart, toAdminEntityBalanceProvider } from './client.model';

@Component({
    selector: 'snelstart-venture-fiscaal-domain-logic-client',
    imports: [
        SharedUiButtonModule,
        SharedUiFormControlsModule,
        SharedUiDetailsModule,
        SharedUiDialogModule,
        SharedUiTabModule,
        SharedUiAlertModule,
        CommonModule,
        BalanceProviderAdministrationSelectionDialogComponent,
    ],
    templateUrl: './client.component.html',
    styleUrl: './client.component.css',
    standalone: true,
})
export class ClientComponent implements OnInit {
    public customer = input.required<CustomerResponse>();
    public dialogVisible = model(false);

    private readonly algDataService = inject(BokkzAlgDataService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly bokkzClient = inject(BokkzClient);
    private readonly kolommenbalansService = inject(KolommenbalansService);

    protected readonly booleanOptions = BOOLEAN_OPTIONS;
    protected readonly bedrijfsklasseOptions = BEDRIJFSKLASSE_OPTIONS;
    protected readonly sbiCodeOptions = SBI_CODE_OPTIONS;
    protected tabIndex = 0;

    protected clientFormGroup = createClientFormGroup();
    protected customerFormGroup = createCustomerFormGroup();
    protected administrationsFormGroup = createAdministrationsFormGroup();

    protected readonly client$: Observable<ClientEntity> = this.algDataService.clientEntity$.pipe(
        filter((client): client is ClientEntity => !!client?.sstRelationId),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    protected readonly intermediary$: Observable<IntermediaryEntity> = this.algDataService.intermediaryEntity$.pipe(
        filter((data): data is IntermediaryEntity => !!data?.sstPrimaryAdminId),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    protected readonly snelStartAdministrations$ = this.kolommenbalansService.getAdministraties().pipe(
        map(
            (snelStartAdministrations) =>
                snelStartAdministrations?.map((snelStartAdministration) => toAdminEntitySnelStart(snelStartAdministration)) ?? [],
        ),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    protected readonly clientAdministrations$ = this.client$.pipe(
        map((client) => client.adminEntities ?? []),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    protected readonly administrationOptions$ = combineLatest([
        this.snelStartAdministrations$,
        this.administrationsFormGroup.valueChanges.pipe(startWith(null)),
    ]).pipe(
        map(([snelStartAdministrations]) => {
            const administrationOptions: SelectFormControlItem<string>[] = [];

            snelStartAdministrations.forEach((snelStartAdministration) => {
                if (
                    this.administrationsFormGroup.controls.administrations.controls.find(
                        (administrationFormGroup) =>
                            administrationFormGroup.controls.providerId.value == null &&
                            administrationFormGroup.controls.environmentId.value == null &&
                            administrationFormGroup.controls.administrationId.value == snelStartAdministration.administrationId,
                    ) == null
                ) {
                    administrationOptions.push({
                        value: snelStartAdministration.administrationId,
                        label: snelStartAdministration.name,
                    } as SelectFormControlItem<string>);
                }
            });

            return administrationOptions;
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    protected readonly canAddAdministrationFormGroup$ = combineLatest([
        this.administrationOptions$,
        this.administrationsFormGroup.valueChanges.pipe(startWith(null)),
    ]).pipe(
        map(([administrationOptions]) => {
            return administrationOptions.length > 0
              && !this.administrationsFormGroup.controls.administrations.getRawValue()
                .some(administration => administration.administrationId == null);
        })
    );

    constructor() {
        // Update customerFormGroup when other customer is loaded
        toObservable(this.customer).pipe(
            filter((customer) => customer?.id != null),
            distinctUntilChanged(
                (previousCustomer, currentCustomer) =>
                    previousCustomer.id === currentCustomer.id
            ),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe({
            next: (customer: CustomerResponse) => {
                console.debug(`ClientComponent.Customer: ${customer.name}`, customer);
                this.customerFormGroup.reset(customer);
            },
        });
    }

    ngOnInit() {
        // Store clientFormGroup changes
        this.clientFormGroup.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.saveForm(false);
        });

        // Validate taxIdWageTax on wageTax change
        this.clientFormGroup.controls.wageTax.valueChanges.pipe(
            startWith(this.clientFormGroup.controls.wageTax.value),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(
            () => this.clientFormGroup.controls.taxIdWageTax.updateValueAndValidity({ emitEvent: false })
        );

        // UpperCase loonheffingennummer on change
        this.clientFormGroup.controls.taxIdWageTax.valueChanges.pipe(
          takeUntilDestroyed(this.destroyRef)
        ).subscribe(loonheffingennummer => {
            const loonheffingennummerUpperCase = loonheffingennummer?.toUpperCase() ?? '';

            if (loonheffingennummer !== loonheffingennummerUpperCase) {
                this.clientFormGroup.controls.taxIdWageTax.setValue(loonheffingennummerUpperCase, { emitEvent: false });
            }
        });

        // Validate taxIdVat on valueAddedTax change
        this.clientFormGroup.controls.valueAddedTax.valueChanges.pipe(
            startWith(this.clientFormGroup.controls.valueAddedTax.value),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(
            () => this.clientFormGroup.controls.taxIdVat.updateValueAndValidity({ emitEvent: false })
        );

        // UpperCase omzetbelastingnummer on change
        this.clientFormGroup.controls.taxIdVat.valueChanges.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe((omzetbelastingnummer) => {
            const omzetbelastingnummerUpperCase = omzetbelastingnummer?.toUpperCase() ?? '';

            if (omzetbelastingnummer !== omzetbelastingnummerUpperCase) {
                this.clientFormGroup.controls.taxIdVat.setValue(omzetbelastingnummerUpperCase, { emitEvent: false });
            }
        });

        // Update clientFormGroup and administrationsFormGroup when other Client is loaded
        this.client$.pipe(
            distinctUntilChanged(
                (previousClient, currentClient) =>
                    previousClient.id === currentClient.id
            ),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe({
            next: (client: ClientEntity) => {
                console.debug(`ClientComponent.Client: ${client.name}`, client);

                this.clientFormGroup.reset(client);

                this.administrationsFormGroup.controls.administrations.clear();

                (client.adminEntities ?? []).forEach((administration) => {
                    const administrationFormGroup = createAdministrationFormGroup();
                    patchAdministrationFormGroup(administrationFormGroup, administration);
                    this.administrationsFormGroup.controls.administrations.push(administrationFormGroup);
                });
            },
        });
    }

    public onSelectTab(index: number) {
        this.tabIndex = index;
    }

    protected addAdministrationFormGroup(): void {
        this.administrationsFormGroup.controls.administrations.push(createAdministrationFormGroup());
    }

    protected removeAdministrationFormGroup(index: number): void {
        this.administrationsFormGroup.controls.administrations.removeAt(index);
    }

    public selectSnelStartAdministration(index: number): void {
        const administrationFormGroup = this.administrationsFormGroup.controls.administrations.at(index);

        combineLatest([this.snelStartAdministrations$, this.clientAdministrations$])
            .pipe(
                take(1),
                tap(([snelStartAdministrations, clientAdministrations]) => {
                    const snelStartAdministration =
                        snelStartAdministrations.find(
                            (snelStartAdministration) =>
                                snelStartAdministration.administrationId === administrationFormGroup.controls.administrationId.value,
                        ) ?? null;

                    const clientAdministration =
                        clientAdministrations.find(
                            (clientAdministration) =>
                                clientAdministration.administrationId === administrationFormGroup.controls.administrationId.value,
                        ) ?? null;

                    administrationFormGroup.patchValue({
                        id: clientAdministration?.id ?? null,
                        name: snelStartAdministration?.name ?? null,
                        providerId: null,
                        environmentId: null,
                        rgs: snelStartAdministration?.rgs ?? null,
                    });
                }),
            )
            .subscribe();
    }

    public saveForm(close: boolean): void {
        const client = this.clientFormGroup.getRawValue() as ClientEntity;

        client.adminEntities = this.administrationsFormGroup.controls.administrations
            .getRawValue()
            .filter((administration) => administration.administrationId != null) as AdminEntity[];

        this.algDataService.updateClient(client);

        if (close) {
            this.bokkzClient
                .postClient(client)
                .pipe(take(1))
                .subscribe(() => {
                    this.dialogVisible.set(false);
                });
        }
    }

    public hasBalanceProviderAdministration() {
        return (
            this.administrationsFormGroup.controls.administrations.controls.find(
                (administrationsFormGroup) => administrationsFormGroup.controls.providerId.value != null,
            ) != null
        );
    }

    protected balanceProviderAdministrationSelected(context: BalanceProviderAdministrationSelectionContext): void {
        console.debug('Balance Provider Administration selected', context);

        if (
            this.administrationsFormGroup.controls.administrations.controls.find(
                (formGroup) =>
                    formGroup.controls.providerId.value === context.balanceProvider.id &&
                    formGroup.controls.environmentId.value === context.environment?.id &&
                    formGroup.controls.administrationId.value === context.administration.id,
            )
        ) {
            console.warn('Administration already added', context);
            return;
        }

        const administrationFormGroup = createAdministrationFormGroup();
        patchAdministrationFormGroup(administrationFormGroup, toAdminEntityBalanceProvider(context));
        this.administrationsFormGroup.controls.administrations.push(administrationFormGroup);
    }
}
